import React from 'react';
import classNames from 'classnames';
import styles from './AreenaFooter.module.scss';
import YleLogo from 'assets/yle.svg';
import YleArenanLogo from 'assets/arenan.svg';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { useTranslation } from 'hooks/useTranslation';
import { Container } from 'components/Container';
import { getOtherLanguageURL } from 'utils/url';
import { Control } from 'components/Controls/Control';
import { DSLink } from 'components/DSLink';
import { Link } from 'components/Link/Link';
import { useRouter } from 'next/router';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

export const FooterFinnish: React.FunctionComponent = () => {
  const { areenaService } = useAreenaService();
  const t = useTranslation();
  const { asPath: path, query } = useRouter();

  const headingId = 'footer-heading';

  const swedishAlternativeUrl = getOtherLanguageURL(
    path,
    query,
    'fi',
    areenaService
  );

  return (
    <footer aria-labelledby={headingId} className={styles.footer}>
      <VisuallyHidden asChild>
        <h2 id={headingId}>{t('footer')}</h2>
      </VisuallyHidden>
      <Container>
        <div className={classNames(styles.areenaLinksSection)}>
          <Link
            className={styles.areenaLink}
            lang="sv"
            pointer={{
              uri: swedishAlternativeUrl,
            }}
          >
            <YleArenanLogo aria-hidden className={styles.areenaLogo} />
            <div className={styles.areenaLinkText}>
              {t('otherLanguageLink')}
            </div>
          </Link>
        </div>
        <div className={styles.generalLinksSection}>
          <section className={styles.subLinksSection}>
            <h3 className={styles.subTitle}>{t('footerSupportTitle')}</h3>
            <ul className={styles.linksContainer}>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://yle.fi/a/20-10005829`,
                    type: 'external-html',
                  }}
                  text={t('tvApps')}
                  textSize="s"
                />
              </li>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://yle.fi/a/20-10005823`,
                    type: 'external-html',
                  }}
                  text={t('androidApps')}
                  textSize="s"
                />
              </li>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://yle.fi/a/20-10005825`,
                    type: 'external-html',
                  }}
                  text={t('iPhoneAndiPad')}
                  textSize="s"
                />
              </li>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://yle.fi/areena-ohjeet`,
                    type: 'external-html',
                  }}
                  text={t('allInstructions')}
                  textSize="s"
                />
              </li>
            </ul>
          </section>
          <section className={styles.subLinksSection}>
            <h3 className={styles.subTitle}>{t('footerSomeTitle')}</h3>
            <ul className={styles.linksContainer}>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://www.facebook.com/yleareena`,
                    type: 'external-html',
                  }}
                  text={t('facebook')}
                  textSize="s"
                />
              </li>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://www.instagram.com/yleareena`,
                    type: 'external-html',
                  }}
                  text={t('instagram')}
                  textSize="s"
                />
              </li>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://www.tiktok.com/@yleareena`,
                    type: 'external-html',
                  }}
                  text={t('tikTok')}
                  textSize="s"
                />
              </li>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://www.youtube.com/c/yleareena`,
                    type: 'external-html',
                  }}
                  text={t('youtube')}
                  textSize="s"
                />
              </li>
            </ul>
          </section>
          <section className={styles.subLinksSection}>
            <h3 className={styles.subTitle}>{t('footerFeedbackTitle')}</h3>
            <ul className={styles.linksContainer}>
              <li>
                <DSLink
                  pointer={{
                    uri: `https://asiakaspalvelu.yle.fi/`,
                    type: 'external-html',
                  }}
                  text={t('customerService')}
                  textSize="s"
                />
              </li>
            </ul>
          </section>
        </div>
      </Container>
      <div className={styles.yleFiLinkContainer}>
        <Container>
          <Link
            pointer={{ uri: `https://yle.fi`, type: 'external-html' }}
            className={styles.yleFiLink}
          >
            <div className={styles.yleFiLinkContent}>
              <YleLogo aria-label="Yle" className={styles.yleLogo} />
              <span className={styles.yleLinkText}>{t('yleUrl')}</span>
            </div>
          </Link>
        </Container>
      </div>
      <div className={styles.yleRelatedLinkContainer}>
        <Container>
          <ul className={styles.yleRelatedLinks}>
            <li>
              <DSLink
                pointer={{
                  uri: `https://yle.fi/yleisradio`,
                  type: 'external-html',
                }}
                text={t('yleCorporation')}
                textSize="s"
                theme="light"
              />
            </li>
            <li>
              <DSLink
                pointer={{
                  uri: `https://yle.fi/a/20-10009548`,
                  type: 'external-html',
                }}
                text={t('yleContactInfo')}
                textSize="s"
                theme="light"
              />
            </li>
            <li>
              <DSLink
                pointer={{
                  uri: `https://yle.fi/a/20-212949`,
                  type: 'external-html',
                }}
                text={t('cookiePolicy')}
                textSize="s"
                theme="light"
              />
            </li>
            <li>
              <Control
                as={'link'}
                controlKey={'cookieSettings'}
                control={{
                  type: 'activator',
                  title: t('cookieSettings'),
                  tag: 'cookie-consent',
                  functionality: {
                    id: 'cookie-consent',
                  },
                }}
              />
            </li>
            <li>
              <DSLink
                pointer={{
                  uri: `https://yle.fi/a/20-213022`,
                  type: 'external-html',
                }}
                textSize="s"
                theme="light"
                text={t('privacyPolicy')}
              />
            </li>
            <li>
              <DSLink
                pointer={{
                  uri: `https://yle.fi/a/20-10008761`,
                  type: 'external-html',
                }}
                textSize="s"
                theme="light"
                text={t('common_accessibilitystatement')}
              />
            </li>
          </ul>
        </Container>
      </div>
    </footer>
  );
};
